import axios, { isAxiosError } from "axios";
import { parseISO } from "date-fns";
import url from "../constants";
import qs from "qs";
import { isStandaloneRouteFromHash } from "../Util/standaloneRoutes";
import { getStoredAccessToken, getStoredRefreshToken, setStoredTokens } from "../Util/secureStorageSession";
import { clearStoragePreservingBrowserBranding } from "../Template/custom-styling/applyBrowserBranding";

declare module "axios" {
	interface AxiosRequestConfig {
		_retry?: boolean;
		requestTimestamp?: string;
	}
}

export const DASHLY_REQUEST_TIMESTAMP_HEADER = "X-Dashly-Request-Timestamp";

let isRequestRefreshing = false;
let requestRefreshPromise = null;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

const api = axios.create({
    baseURL: url,
    headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
        "Accept-Language": "",
    },
    params: {},
    paramsSerializer: {
        serialize: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
    },
})

const ISODateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

const isIsoDateString = (value: unknown): value is string => {
    return typeof value === "string" && ISODateFormat.test(value);
};

const handleDates = (data: unknown) => {
    if (isIsoDateString(data)) return parseISO(data);
    if (data === null || data === undefined || typeof data !== "object") return data;

    for (const [key, val] of Object.entries(data)) {
        // @ts-expect-error this is a hack to make the type checker happy
        if (isIsoDateString(val)) data[key] = parseISO(val);
        else if (typeof val === "object") handleDates(val);
    }

    return data
};

api.interceptors.request.use(
    (config) => {
        const token = getStoredAccessToken();
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token;
            config.headers["Access-Control-Allow-Origin"] = "*";
            config.headers["Access-Control-Allow-Methods"] = "GET,PUT,POST,DELETE,PATCH,OPTIONS";
        } else {
            config.headers["Access-Control-Allow-Origin"] = "*";
            config.headers["Access-Control-Allow-Methods"] = "GET,PUT,POST,DELETE,PATCH,OPTIONS";
        }
        if (!config._retry) {
            const existingTimestamp = config.headers.get(DASHLY_REQUEST_TIMESTAMP_HEADER);
            const timestamp = typeof existingTimestamp === "string" && existingTimestamp.trim().length > 0
                ? existingTimestamp
                : new Date().toISOString();
            config.headers.set(DASHLY_REQUEST_TIMESTAMP_HEADER, timestamp);
            config.requestTimestamp = timestamp;
        }
        return config;
    },
    (error) => Promise.reject(error)
)

const processRequestQueue = (error: unknown, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

const shouldForceLogoutOnRefreshFailure = (error: any) => {
	const status = error?.response?.status;
	const internalCode = error?.response?.data?.internalCode;

	return status === 401 || status === 403 || internalCode === 'IMSECTKINV';
};

api.interceptors.response.use(response => response, async (error) => {
		if (isAxiosError(error) && (error.code === "ERR_CANCELED" || error.name === "CanceledError")) {
			return Promise.reject(error);
		}

		const originalRequest = error.config;

		if (error.response?.status === 401 && error.response?.data?.internalCode === 'IMSECTKINV' && !originalRequest._retry) {
			originalRequest._retry = true;

			if (isRequestRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then(token => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					return api(originalRequest);
				});
			}

			isRequestRefreshing = true;
			requestRefreshPromise = axios.post(url + 'v1/auth/refresh-token', {
				refreshToken: getStoredRefreshToken()
			});

			try {
				const response = await requestRefreshPromise;
				const { accessToken, refreshToken } = response.data.data;

				setStoredTokens(accessToken, refreshToken);

				processRequestQueue(null, accessToken);

				originalRequest.headers.Authorization = `Bearer ${accessToken}`;
				return api(originalRequest);

			} catch (err) {
				processRequestQueue(err, null);

				if (shouldForceLogoutOnRefreshFailure(err)) {
					clearStoragePreservingBrowserBranding();
					const hash = window.location.hash || "";
					if (!isStandaloneRouteFromHash(hash)) {
						window.location.href = "/";
					}
				}
				return Promise.reject(err);
			} finally {
				isRequestRefreshing = false;
				requestRefreshPromise = null;
			}
		}

		const method = originalRequest?.method?.toUpperCase?.() ?? '?';
		const requestUrl = originalRequest?.url ?? '?';
		const status = error?.response?.status;
		const internalCode = error?.response?.data?.internalCode;
		console.error(`API ${method} ${requestUrl} failed`, {
			status,
			internalCode,
			message: error?.response?.data?.message ?? error?.message,
			data: error?.response?.data,
			error,
		});
		return Promise.reject(error);
	}
);

export default api